import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  ListItemIcon,
  Box,
  Divider,
  Typography,
  Avatar,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemAvatar,
  Badge,
  Chip
} from '@mui/material';
import {
  Dashboard,
  ExpandLess,
  ExpandMore,
  Public,
  Business,
  Settings,
  Logout,
  Group,
  History,
  Search,
  AdminPanelSettings,
  LiveTv,
  LibraryBooks,
  Event,
  Flag,
  People
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ drawerWidth = 280 }) => {
  const { user, organisations, selectedOrganisation, logout } = useAuth();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({
    settings: false
  });
  const [flagErrors, setFlagErrors] = useState({});
  const [hoveredGroup, setHoveredGroup] = useState(null);

  // Log user data on mount
  useEffect(() => {
    if (user) {
      console.log('User data:', user);
      console.log('Groups:', user.groups);
      if (user.groups && user.groups.length > 0) {
        console.log('First group flag_url:', user.groups[0].flag_url);
      }
    }
  }, [user]);

  const toggleMenu = (key) => {
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Get groups from user data and sort alphabetically by country then group name
  const groups = user?.groups || [];
  const sortedGroups = [...groups].sort((a, b) => {
    const countryA = a.country_name || '';
    const countryB = b.country_name || '';
    if (countryA !== countryB) {
      return countryA.localeCompare(countryB);
    }
    return (a.group_name || '').localeCompare(b.group_name || '');
  });

  // Group count
  const totalGroups = sortedGroups.length;

  // Flag URL base
  const FLAG_BASE_URL = 'https://525482.8664.wiki/app/uploads/flags/';

  // Handle flag image error
  const handleFlagError = (groupId) => {
    setFlagErrors(prev => ({ ...prev, [groupId]: true }));
  };

  // Main menu items with submenu
  const mainMenuItems = [
    {
      key: 'settings',
      label: 'Settings',
      icon: <Settings />,
      expandable: true,
      children: [
        { key: 'administrators', label: 'Administrators', icon: <AdminPanelSettings />, link: '/administrators' }
      ]
    },
    {
      key: 'logs',
      label: 'Logs',
      icon: <History />,
      link: '/logs'
    },
    {
      key: 'wikiwaste-search',
      label: 'Wikiwaste Search',
      icon: <Search />,
      link: '/wikiwaste-search'
    }
  ];

  // Sub-menu items for each group
  const groupSubMenus = [
    { key: 'administrators', label: 'Group Administrators', icon: <AdminPanelSettings />, link: '/group-administrators' },
    { key: 'live-adverts', label: 'Live Adverts', icon: <LiveTv />, link: '/live-adverts' },
    { key: 'advert-library', label: 'Advert Library', icon: <LibraryBooks />, link: '/advert-library' },
    { key: 'events', label: 'Events', icon: <Event />, link: '/events' },
    { key: 'logs', label: 'Logs', icon: <History />, link: '/group-logs' }
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: 'none', sm: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
          borderRight: '1px solid #e2e8f0',
          overflowX: 'hidden'
        }
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Public sx={{ fontSize: 28 }} />
          Barriers DB
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Main Menu Items - Settings (with submenu), Logs, WikiWaste Search */}
      <List sx={{ px: 1 }}>
        {mainMenuItems.map((item) => {
          if (item.expandable) {
            const isSettingsOpen = openMenus.settings || false;
            return (
              <Box key={item.key}>
                <ListItemButton 
                  onClick={() => toggleMenu('settings')}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  {isSettingsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={isSettingsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton 
                        key={child.key}
                        component={Link} 
                        to={child.link}
                        selected={isActive(child.link)}
                        sx={{ 
                          pl: 4, 
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(245, 124, 0, 0.08)',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={child.label}
                          primaryTypographyProps={{ fontSize: '0.85rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          }
          
          return (
            <ListItemButton 
              key={item.key}
              component={Link} 
              to={item.link}
              selected={isActive(item.link)}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ my: 2 }} />

      {/* Groups Section - Each group as one line with flag + country + group name */}
      <Box sx={{ px: 1, overflow: 'auto', flex: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          px: 1, 
          py: 1
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            GROUPS
          </Typography>
          <Chip 
            label={totalGroups} 
            size="small" 
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 20,
              minWidth: 20
            }} 
          />
        </Box>
        
        {sortedGroups.length > 0 ? (
          sortedGroups.map((group) => {
            const groupMenuKey = `group-${group.group_id}`;
            const isGroupOpen = openMenus[groupMenuKey] || false;
            const isHovered = hoveredGroup === group.group_id;
            
            // Build group display name: "Country - Group Name"
            const countryName = group.country_name || 'Unknown';
            const groupName = group.group_name || group.group_name_native || 'Unnamed Group';
            const displayName = `${countryName} - ${groupName}`;
            
            // Check if flag_url exists and build the flag URL
            const flagUrl = group.flag_url ? `${FLAG_BASE_URL}${group.flag_url}` : null;
            const hasFlagError = flagErrors[group.group_id];
            
            return (
              <Box key={group.group_id} sx={{ mb: 0.5 }}>
                {/* Group header with flag + country + group name */}
                <ListItemButton 
                  onClick={() => toggleMenu(groupMenuKey)}
                  onMouseEnter={() => setHoveredGroup(group.group_id)}
                  onMouseLeave={() => setHoveredGroup(null)}
                  sx={{ 
                    borderRadius: 2,
                    py: 1.5,
                    bgcolor: isGroupOpen ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.05)',
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Box
                      sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: isHovered ? 'primary.main' : 'transparent',
                        boxShadow: isHovered ? '0 0 20px rgba(25, 118, 210, 0.3)' : 'none',
                        transition: 'all 0.3s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.05)',
                        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                      }}
                    >
                      {flagUrl && !hasFlagError ? (
                        <Box
                          component="img"
                          src={flagUrl}
                          alt={countryName}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                          onError={() => handleFlagError(group.group_id)}
                        />
                      ) : (
                        <Flag sx={{ fontSize: 24, color: 'text.secondary' }} />
                      )}
                      
                      {/* Hover overlay effect */}
                      {isHovered && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(25, 118, 210, 0.15)',
                            transition: 'all 0.3s ease-in-out',
                          }}
                        />
                      )}
                    </Box>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={displayName}
                    primaryTypographyProps={{ 
                      fontSize: '0.9rem',
                      fontWeight: isGroupOpen ? 600 : 400,
                      color: isHovered ? 'primary.main' : 'text.primary',
                      transition: 'color 0.2s ease-in-out',
                    }}
                  />
                  {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                {/* Group Sub-menus */}
                <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {groupSubMenus.map((subItem) => (
                      <ListItemButton 
                        key={`${group.group_id}-${subItem.key}`}
                        component={Link}
                        to={`${subItem.link}/${group.group_id}`}
                        sx={{ 
                          pl: 6, 
                          py: 0.75,
                          borderRadius: 2,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(245, 124, 0, 0.08)',
                            transform: 'translateX(4px)',
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={subItem.label}
                          primaryTypographyProps={{ 
                            fontSize: '0.8rem',
                            color: 'text.secondary'
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No groups assigned
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.role || 'User'}
            </Typography>
          </Box>
          <Tooltip title="Logout">
            <IconButton size="small" onClick={logout}>
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;